import type { TicketKind, TicketStatus } from './types';

export const KINDS: { id: TicketKind; title: string; hint: string }[] = [
  { id: 'bug', title: 'что-то не работает', hint: 'ошибка, зависание, не тот кэф' },
  { id: 'idea', title: 'идея или пожелание', hint: 'что добавить или сделать удобнее' },
  { id: 'other', title: 'другое', hint: 'всё, что не подошло выше' },
];

export const KIND_SHORT: Record<TicketKind, string> = { bug: 'не работает', idea: 'идея', other: 'другое' };

export const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'ждёт ответа',
  work: 'в работе',
  fixed: 'исправлено — проверьте',
  closed: 'закрыто',
};

/** Для стола: те же статусы глазами того, кто отвечает. */
export const STATUS_DESK: Record<TicketStatus, string> = {
  open: 'ждёт ответа',
  work: 'в работе',
  fixed: 'ждём проверку',
  closed: 'закрыто',
};

export const PLACEHOLDER: Record<TicketKind, string> = {
  bug: 'Что делали → что ожидали → что получилось.\nНапример: открыл вилку Thunderpick × Polymarket на «лайве», нажал «открыть сразу» — купон Polymarket открылся на другом исходе. Chrome, AdsPower.',
  idea: 'Что добавить или поменять и зачем.\nНапример: фильтр «только мои конторы» на прематче — сейчас каждый раз снимаю галочки заново.',
  other: 'Опишите, что хотите сказать.',
};

/** Сторона, которая смотрит: человек на сайте или стол. */
export type Side = 'user' | 'staff';

export function eventLine(event: TicketStatus, author: 'user' | 'staff', side: Side): string {
  if (author === 'user') {
    if (event === 'closed') return side === 'user' ? 'вы отметили: работает, обращение закрыто' : 'человек отметил: работает, закрыл';
    if (event === 'open') return side === 'user' ? 'обращение снова открыто' : 'человек ответил — снова ждёт ответа';
    return STATUS_LABEL[event];
  }
  if (event === 'work') return 'взяли в работу';
  if (event === 'fixed') return side === 'user' ? 'исправили — проверьте, пожалуйста' : 'отмечено: исправлено, ждём проверку';
  if (event === 'closed') return 'обращение закрыто';
  return 'вернули в работу';
}

export type Depth = { level: 0 | 1 | 2 | 3; label: string };

/** Подсказка «насколько подробно». Не запрет — просто подталкивает дописать. */
export function depth(text: string, shots: number): Depth {
  const n = text.trim().length;
  if (n === 0 && shots === 0) return { level: 0, label: '' };
  if (n >= 200 || (n >= 100 && shots > 0)) return { level: 3, label: 'подробно — так разберёмся быстрее всего' };
  if (n >= 60 || (n >= 20 && shots > 0)) return { level: 2, label: 'хорошо — пара деталей сделает ещё лучше' };
  return { level: 1, label: 'коротко — добавьте, что делали и что увидели' };
}

const pad = (n: number): string => String(n).padStart(2, '0');

export function ago(iso: string, now = Date.now()): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '';
  const sec = Math.max(0, Math.round((now - at) / 1000));
  if (sec < 60) return 'только что';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} мин назад`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} ч назад`;
  const d = new Date(at);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function clock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function personLine(who: { name: string | null; username: string | null } | undefined, id: string): string {
  if (who?.name && who.username) return `${who.name} · @${who.username}`;
  if (who?.name) return who.name;
  if (who?.username) return `@${who.username}`;
  return id;
}
